import { useEffect, useState } from "react";
import { useAuth } from "@frontegg/react";
import { getAccessToken } from "../lib/auth-token";

const cache = new Map<string, boolean>();
const inflight = new Map<string, Promise<boolean>>();

function fetchIsAdmin(email: string): Promise<boolean> {
  const existing = inflight.get(email);
  if (existing) return existing;
  const p = (async () => {
    try {
      const token = getAccessToken();
      const headers: Record<string, string> = {};
      if (token) headers.Authorization = `Bearer ${token}`;
      const res = await fetch("/api/admin/me", { headers });
      if (!res.ok) return false;
      const data = await res.json();
      return !!data.is_admin;
    } catch {
      return false;
    }
  })().then((v) => {
    cache.set(email, v);
    inflight.delete(email);
    return v;
  });
  inflight.set(email, p);
  return p;
}

export function useIsAdmin(): { isAdmin: boolean; loading: boolean } {
  const { isAuthenticated, user } = useAuth();
  const email = user?.email?.toLowerCase() ?? "";

  const [isAdmin, setIsAdmin] = useState<boolean>(
    email ? cache.get(email) ?? false : false,
  );
  const [loading, setLoading] = useState<boolean>(
    !!email && !cache.has(email),
  );

  useEffect(() => {
    if (!isAuthenticated || !email) {
      setIsAdmin(false);
      setLoading(false);
      return;
    }
    if (cache.has(email)) {
      setIsAdmin(cache.get(email)!);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setIsAdmin(false);
    setLoading(true);
    fetchIsAdmin(email).then((v) => {
      if (cancelled) return;
      setIsAdmin(v);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, email]);

  return { isAdmin, loading };
}
